import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton, MenuItem, Select, FormControl, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import StarIcon from '@material-ui/icons/Star';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import { COLORS } from '../../theme';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';

const API_BASE = '/planner-api';

// Tipos de local — as CHAVES são as do enum LocationType na BD.
const TYPES = [
  { key: 'garage', label: 'Base / Garagem', icon: '🏠', color: '#2563eb', bg: '#eaf2fe' },
  { key: 'fuel', label: 'Local para Abastecer', icon: '⛽', color: '#dc2626', bg: '#fee2e2' },
  { key: 'rest_area', label: 'Área de Descanso', icon: '🛏️', color: '#7c3aed', bg: '#f3effd' },
  { key: 'warehouse', label: 'Armazém', icon: '📦', color: '#b45309', bg: '#fff1e3' },
  { key: 'safe_point', label: 'Ponto Seguro', icon: '🛡️', color: '#0d7a72', bg: '#e6f7f6' },
  { key: 'overnight', label: 'Pernoita', icon: '🌙', color: '#4338ca', bg: '#eeeefc' },
  { key: 'poi', label: 'Ponto de Interesse', icon: '📍', color: '#64748b', bg: '#f1f5f9' },
  { key: 'other', label: 'Outros', icon: '📌', color: '#64748b', bg: '#f1f5f9' },
];
const typeMeta = (t) => TYPES.find(x => x.key === t) || TYPES[TYPES.length - 1];

const EMPTY = {
  name: '', type: 'poi', latitude: '', longitude: '', address: '', notes: '',
  isPreferred: false, includeInRoute: false, isDeparture: false,
};

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22,
    background: 'var(--adm-header-bg, #e2f1e9)', border: '1px solid var(--adm-header-border, #bfe0d1)', borderRadius: 14, padding: '18px 22px',
  },
  titleIcon: { width: 44, height: 44, borderRadius: 12, background: 'var(--adm-brand-soft, rgba(47,93,80,0.10))', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: '1.72rem', fontWeight: 700, color: COLORS.text, letterSpacing: '-0.02em' },
  subtitle: { fontSize: '0.78rem', color: COLORS.textSecondary, marginTop: 2 },
  addBtn: {
    marginLeft: 'auto', background: 'var(--adm-brand, #2f5d50)', color: '#fff', fontWeight: 600, fontSize: '0.82rem',
    borderRadius: 11, padding: '9px 22px', textTransform: 'none',
    boxShadow: '0 4px 16px rgba(47,93,80,0.3)',
    '&:hover': { background: 'var(--adm-brand-dark, #274c41)' },
    '&:disabled': { background: '#cbd5e1', color: '#fff', boxShadow: 'none' },
  },
  orgBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  locCard: {
    position: 'relative', background: 'var(--adm-card, #fff)', borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)',
    padding: '18px 20px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(52,71,103,0.12)' },
  },
  cardActions: { position: 'absolute', top: 14, right: 14, display: 'flex' },
  tableCard: {
    background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'visible',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '14px 20px', fontSize: '0.66rem', fontWeight: 700,
    color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em',
    borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'var(--adm-thead-bg, #f8fafc)',
    position: 'sticky', top: 0, zIndex: 2,
  },
  td: { padding: '14px 20px', fontSize: '0.84rem', color: COLORS.text, borderBottom: '1px solid rgba(0,0,0,0.03)' },
  row: { transition: 'background 0.15s', '&:hover': { background: 'var(--adm-hover, #f8fafc)' }, '&:last-child td': { borderBottom: 'none' } },
  typeBadge: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 700 },
  flag: { fontSize: '0.62rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fff1e3', color: '#c2410c', border: '1px solid #fdd9bd', marginLeft: 6 },
  coords: { fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: '0.74rem', color: COLORS.textMuted },
  // ── Células ricas da tabela ────────────────────────────────────────
  rowIcon: {
    width: 34, height: 34, borderRadius: 9, flexShrink: 0, fontSize: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  rowNote: {
    fontSize: '0.68rem', color: COLORS.textMuted, maxWidth: 220,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  addr: {
    fontSize: '0.78rem', color: COLORS.textSecondary,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  dash: { fontSize: '0.74rem', color: 'var(--adm-text-muted, #cbd5e1)', fontStyle: 'italic' },
  actionsCell: { display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
  empty: { padding: 50, textAlign: 'center', color: COLORS.textMuted, fontSize: '0.86rem' },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  dialog: { background: 'var(--adm-card, #fff)', borderRadius: 18, width: 720, maxWidth: '94vw', maxHeight: '92vh', boxShadow: '0 24px 70px rgba(0,0,0,0.32)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  dialogHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 26px 14px', borderBottom: '1px solid var(--adm-border, #eef2f7)' },
  dialogTitle: { fontSize: '1.15rem', fontWeight: 700, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 10 },
  dialogBody: { padding: '20px 26px', overflowY: 'auto' },
  section: { background: 'var(--adm-section-bg, #f3f8f5)', border: '1px solid var(--adm-section-border, #e2efe8)', borderRadius: 14, padding: '18px 20px', marginBottom: 16 },
  sectionTitle: { fontSize: '0.72rem', fontWeight: 800, color: 'var(--adm-brand, #2f5d50)', textTransform: 'uppercase', letterSpacing: '0.08em' },
  sectionHint: { fontSize: '0.74rem', color: 'var(--adm-brand-mid, #5a8577)', fontWeight: 500, marginTop: 3, marginBottom: 14, lineHeight: 1.45 },
  formRow: { display: 'flex', gap: 14, marginBottom: 14, flexWrap: 'wrap' },
  field: { flex: 1, minWidth: 150 },
  label: { fontSize: '0.62rem', fontWeight: 700, color: '#6b8a80', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
  input: { width: '100%', border: '1.5px solid var(--adm-input-border, #d9e6df)', borderRadius: 9, padding: '10px 13px', fontSize: '0.85rem', fontWeight: 600, color: 'var(--adm-text, #1e293b)', outline: 'none', background: 'var(--adm-input-bg, #fff)', boxSizing: 'border-box' },
  checkGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  check: { display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.8rem', fontWeight: 600, color: 'var(--adm-text, #334155)', background: 'var(--adm-input-bg, #fff)', border: '1.5px solid var(--adm-input-border, #d9e6df)', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none' },
  checkOn: { background: 'var(--adm-brand-soft, rgba(47,93,80,0.10))', borderColor: 'var(--adm-brand, #2f5d50)', color: 'var(--adm-brand, #2f5d50)' },
  dialogFooter: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '16px 26px', borderTop: '1px solid var(--adm-border, #e2e8f0)', background: 'var(--adm-hover, #f8fafc)' },
  cancelBtn: { fontSize: '0.8rem', textTransform: 'none', color: 'var(--adm-text-dim, #64748b)', borderColor: 'var(--adm-border, #e2e8f0)', borderRadius: 8, padding: '6px 16px' },
  saveBtn: { fontSize: '0.8rem', textTransform: 'none', background: 'var(--adm-brand, #2f5d50)', color: '#fff', borderRadius: 8, padding: '6px 20px', '&:hover': { background: 'var(--adm-brand-dark, #274c41)' }, '&:disabled': { background: '#e2e8f0', color: '#94a3b8' } },
  err: { color: 'var(--adm-danger, #dc2626)', fontSize: '0.78rem', fontWeight: 600, alignSelf: 'center', marginRight: 'auto' },
  iconBtn: { width: 30, height: 30, background: 'rgba(47,93,80,0.06)', borderRadius: 8, '&:hover': { background: 'rgba(47,93,80,0.14)' } },
  delBtn: { width: 30, height: 30, background: 'rgba(239,68,68,0.06)', borderRadius: 8, marginLeft: 6, '&:hover': { background: 'rgba(239,68,68,0.14)' } },
}));

function AdminLocations({ token }) {
  const classes = useStyles();
  const auth = { Authorization: `Bearer ${token}` };
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [view, setView] = useAdminView('locations');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/clients`, { headers: auth });
        if (res.ok) setClients(await res.json());
      } catch (e) { console.error('Clients load error:', e); }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  async function loadLocations(cid) {
    if (!cid) { setLocations([]); return; }
    setListLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${cid}/locations`, { headers: auth });
      setLocations(res.ok ? await res.json() : []);
    } catch (e) { console.error('Locations load error:', e); setLocations([]); }
    setListLoading(false);
  }

  function pickClient(cid) { setClientId(cid); loadLocations(cid); }

  function openNew() { setError(''); setEditing({ ...EMPTY }); }
  function openEdit(l) {
    setError('');
    setEditing({
      ...EMPTY, ...l,
      latitude: String(l.latitude ?? ''), longitude: String(l.longitude ?? ''),
      address: l.address || '', notes: l.notes || '',
    });
  }
  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }));

  async function handleSave() {
    if (!editing.name.trim()) { setError('Nome obrigatório'); return; }
    const lat = Number(editing.latitude), lng = Number(editing.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) { setError('Coordenadas inválidas'); return; }
    setSaving(true); setError('');
    const payload = {
      name: editing.name.trim(), type: editing.type,
      latitude: lat, longitude: lng,
      address: editing.address || null, notes: editing.notes || null,
      isPreferred: editing.isPreferred, includeInRoute: editing.includeInRoute, isDeparture: editing.isDeparture,
    };
    try {
      const url = editing.id
        ? `${API_BASE}/clients/${clientId}/locations/${editing.id}`
        : `${API_BASE}/clients/${clientId}/locations`;
      const res = await fetch(url, {
        method: editing.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (res.ok) { setEditing(null); await loadLocations(clientId); }
      else { const e = await res.json().catch(() => ({})); setError(e.error || 'Erro ao guardar'); }
    } catch { setError('Erro de rede'); }
    setSaving(false);
  }

  async function handleDelete(l) {
    if (!window.confirm(`Apagar o favorito "${l.name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/locations/${l.id}`, { method: 'DELETE', headers: auth });
      if (res.ok) await loadLocations(clientId);
      else window.alert('Não foi possível apagar');
    } catch { window.alert('Erro de rede'); }
  }

  if (loading) return <Box className={classes.loading}><CircularProgress size={36} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>;

  const orgLabel = (c) => c.ecoogpsClientName || c.name;

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.titleIcon}><StarIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 24 }} /></Box>
        <Box>
          <Typography className={classes.title}>Favoritos & POIs</Typography>
          <Typography className={classes.subtitle}>
            {clientId ? `${locations.length} locais nesta Organização` : 'Escolhe uma Organização para ver os seus locais'}
          </Typography>
        </Box>
        <Button className={classes.addBtn} startIcon={<AddIcon />} onClick={openNew} disabled={!clientId}>
          Novo favorito
        </Button>
      </Box>

      <AdminHint
        nodes={[{ text: 'Favoritos', tone: 'orange' }, { text: 'Organização', tone: 'green' }]}
        labels={['pertencem à']}
        note="Os favoritos (bases, postos de abastecimento, áreas de descanso, POIs) são DADOS da Organização — não fazem parte do Perfil, porque têm coordenadas reais e não se partilham entre empresas. Esta página edita exatamente os mesmos locais que o ecrã Favoritos do planner."
      />

      <Box className={classes.orgBar}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 340 }}>
          <InputLabel>Entidade / Organização</InputLabel>
          <Select label="Entidade / Organização" value={clientId} onChange={e => pickClient(e.target.value)}>
            <MenuItem value=""><em>— escolher —</em></MenuItem>
            {clients.map(c => <MenuItem key={c.id} value={c.id}>{orgLabel(c)}</MenuItem>)}
          </Select>
        </FormControl>
        <Box style={{ marginLeft: 'auto' }}>
          <AdminViewToggle view={view} onChange={setView} />
        </Box>
      </Box>

      {!clientId ? (
        <Box className={classes.tableCard}>
          <Typography className={classes.empty}>Escolhe uma Organização acima para gerir os seus favoritos.</Typography>
        </Box>
      ) : listLoading ? (
        <Box className={classes.loading}><CircularProgress size={30} style={{ color: 'var(--adm-brand, #2f5d50)' }} /></Box>
      ) : view === 'cards' ? (
        locations.length === 0 ? (
          <Box className={classes.tableCard}>
            <Typography className={classes.empty}>Esta Organização ainda não tem favoritos.</Typography>
          </Box>
        ) : (
          <Box className={classes.cardsGrid}>
            {locations.map(l => {
              const tm = typeMeta(l.type);
              return (
                <Box key={l.id} className={classes.locCard}>
                  <Box className={classes.cardActions}>
                    <IconButton size="small" className={classes.iconBtn} onClick={() => openEdit(l)}>
                      <EditIcon style={{ fontSize: 15, color: 'var(--adm-brand, #2f5d50)' }} />
                    </IconButton>
                    <IconButton size="small" className={classes.delBtn} onClick={() => handleDelete(l)}>
                      <DeleteIcon style={{ fontSize: 15, color: 'var(--adm-danger, #ef4444)' }} />
                    </IconButton>
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10, paddingRight: 66 }}>
                    <Box style={{ width: 42, height: 42, borderRadius: 12, background: tm.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                      {tm.icon}
                    </Box>
                    <Box style={{ minWidth: 0 }}>
                      <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</Typography>
                      <span className={classes.typeBadge} style={{ background: tm.bg, color: tm.color, marginTop: 2 }}>{tm.label}</span>
                    </Box>
                  </Box>
                  {(l.isDeparture || l.isPreferred || l.includeInRoute) && (
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                      {l.isDeparture && <span className={classes.flag} style={{ marginLeft: 0 }}>⛽ Partida</span>}
                      {l.isPreferred && <span className={classes.flag} style={{ marginLeft: 0, background: '#fef9c3', color: '#a16207', borderColor: '#fde68a' }}>★ Preferido</span>}
                      {l.includeInRoute && <span className={classes.flag} style={{ marginLeft: 0, background: 'var(--adm-brand-soft, #eef5f1)', color: 'var(--adm-brand, #2f5d50)', borderColor: '#d8e9e1' }}>Na rota</span>}
                    </Box>
                  )}
                  <Typography style={{ fontSize: '0.76rem', color: COLORS.textSecondary, marginBottom: 8, lineHeight: 1.4 }}>
                    {l.address || '— sem morada —'}
                  </Typography>
                  <span className={classes.coords}>{Number(l.latitude).toFixed(4)}, {Number(l.longitude).toFixed(4)}</span>
                </Box>
              );
            })}
          </Box>
        )
      ) : (
        <Box className={classes.tableCard}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th}>Local</th>
                <th className={classes.th}>Tipo</th>
                <th className={classes.th}>Papel</th>
                <th className={classes.th}>Morada</th>
                <th className={classes.th}>Coordenadas</th>
                <th className={classes.th} style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {locations.length === 0 && (
                <tr><td className={classes.td} colSpan={6}>
                  <Typography className={classes.empty}>Esta Organização ainda não tem favoritos.</Typography>
                </td></tr>
              )}
              {locations.map(l => {
                const tm = typeMeta(l.type);
                return (
                  <tr key={l.id} className={classes.row}>
                    {/* Local — ícone do tipo + nome + notas */}
                    <td className={classes.td}>
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <Box className={classes.rowIcon} style={{ background: tm.bg }}>{tm.icon}</Box>
                        <Box style={{ minWidth: 0 }}>
                          <Typography style={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.text }}>{l.name}</Typography>
                          {l.notes && <Typography className={classes.rowNote} title={l.notes}>{l.notes}</Typography>}
                        </Box>
                      </Box>
                    </td>
                    <td className={classes.td}>
                      <span className={classes.typeBadge} style={{ background: tm.bg, color: tm.color }}>{tm.label}</span>
                    </td>
                    {/* Papel — os selos que antes se misturavam com o nome */}
                    <td className={classes.td}>
                      {(l.isDeparture || l.isPreferred || l.includeInRoute) ? (
                        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {l.isDeparture && <span className={classes.flag} style={{ marginLeft: 0 }}>⛽ Partida</span>}
                          {l.isPreferred && <span className={classes.flag} style={{ marginLeft: 0, background: '#fef9c3', color: '#a16207', borderColor: '#fde68a' }}>★ Preferido</span>}
                          {l.includeInRoute && <span className={classes.flag} style={{ marginLeft: 0, background: 'var(--adm-brand-soft, #eef5f1)', color: 'var(--adm-brand, #2f5d50)', borderColor: '#d8e9e1' }}>Na rota</span>}
                        </Box>
                      ) : <span className={classes.dash}>—</span>}
                    </td>
                    <td className={classes.td} style={{ maxWidth: 260 }}>
                      {l.address
                        ? <Box className={classes.addr} title={l.address}>{l.address}</Box>
                        : <span className={classes.dash}>— sem morada</span>}
                    </td>
                    <td className={classes.td}>
                      <span className={classes.coords}>{Number(l.latitude).toFixed(4)}, {Number(l.longitude).toFixed(4)}</span>
                    </td>
                    <td className={classes.td}>
                      <Box className={classes.actionsCell}>
                        <IconButton size="small" className={classes.iconBtn} onClick={() => openEdit(l)}>
                          <EditIcon style={{ fontSize: 15, color: 'var(--adm-brand, #2f5d50)' }} />
                        </IconButton>
                        <IconButton size="small" className={classes.delBtn} onClick={() => handleDelete(l)}>
                          <DeleteIcon style={{ fontSize: 15, color: 'var(--adm-danger, #ef4444)' }} />
                        </IconButton>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}

      {editing && (
        <Box className={classes.overlay} onClick={() => !saving && setEditing(null)}>
          <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
            <Box className={classes.dialogHeader}>
              <Box className={classes.dialogTitle}>
                <span style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--adm-brand-soft, rgba(47,93,80,0.08))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <StarIcon style={{ color: 'var(--adm-brand, #2f5d50)', fontSize: 20 }} />
                </span>
                {editing.id ? 'Editar favorito' : 'Novo favorito'}
              </Box>
              <IconButton size="small" onClick={() => setEditing(null)} style={{ color: 'var(--adm-text-muted, #94a3b8)' }}>
                <CloseIcon style={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box className={classes.dialogBody}>
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Identificação</Typography>
                <Typography className={classes.sectionHint}>Como se chama o local e que tipo é — o tipo define o ícone e o agrupamento no planner.</Typography>
                <Box className={classes.formRow}>
                  <Box className={classes.field} style={{ flex: 2 }}>
                    <label className={classes.label}>Nome</label>
                    <input className={classes.input} value={editing.name} onChange={e => set('name', e.target.value)} autoFocus />
                  </Box>
                  <Box className={classes.field}>
                    <label className={classes.label}>Tipo</label>
                    <select className={classes.input} value={editing.type} onChange={e => set('type', e.target.value)}>
                      {TYPES.map(t => <option key={t.key} value={t.key}>{t.icon} {t.label}</option>)}
                    </select>
                  </Box>
                </Box>
                <Box className={classes.formRow} style={{ marginBottom: 0 }}>
                  <Box className={classes.field} style={{ flex: 2 }}>
                    <label className={classes.label}>Morada</label>
                    <input className={classes.input} value={editing.address} onChange={e => set('address', e.target.value)} placeholder="Opcional" />
                  </Box>
                </Box>
              </Box>

              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>Coordenadas</Typography>
                <Typography className={classes.sectionHint}>Latitude e longitude em graus decimais (ex.: -8.8884 / 13.3468).</Typography>
                <Box className={classes.formRow} style={{ marginBottom: 0 }}>
                  <Box className={classes.field}>
                    <label className={classes.label}>Latitude</label>
                    <input className={classes.input} type="number" step="any" value={editing.latitude} onChange={e => set('latitude', e.target.value)} />
                  </Box>
                  <Box className={classes.field}>
                    <label className={classes.label}>Longitude</label>
                    <input className={classes.input} type="number" step="any" value={editing.longitude} onChange={e => set('longitude', e.target.value)} />
                  </Box>
                </Box>
              </Box>

              <Box className={classes.section} style={{ marginBottom: 0 }}>
                <Typography className={classes.sectionTitle}>Papel do local</Typography>
                <Typography className={classes.sectionHint}>"Partida" faz as rotas de combustível arrancarem daqui (o parque). "Preferido" destaca-o nas listas. "Incluir na rota" sugere-o como paragem.</Typography>
                <Box className={classes.checkGrid}>
                  <Box className={`${classes.check} ${editing.isDeparture ? classes.checkOn : ''}`} onClick={() => set('isDeparture', !editing.isDeparture)}>
                    <input type="checkbox" checked={editing.isDeparture} readOnly /> ⛽ Local de partida
                  </Box>
                  <Box className={`${classes.check} ${editing.isPreferred ? classes.checkOn : ''}`} onClick={() => set('isPreferred', !editing.isPreferred)}>
                    <input type="checkbox" checked={editing.isPreferred} readOnly /> ★ Preferido
                  </Box>
                  <Box className={`${classes.check} ${editing.includeInRoute ? classes.checkOn : ''}`} onClick={() => set('includeInRoute', !editing.includeInRoute)}>
                    <input type="checkbox" checked={editing.includeInRoute} readOnly /> Incluir na rota
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className={classes.dialogFooter}>
              {error && <Typography className={classes.err}>{error}</Typography>}
              <Button variant="outlined" className={classes.cancelBtn} onClick={() => setEditing(null)}>Cancelar</Button>
              <Button className={classes.saveBtn} onClick={handleSave} disabled={saving || !editing.name.trim()} startIcon={<SaveIcon style={{ fontSize: 16 }} />}>
                {saving ? 'A guardar...' : 'Guardar'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default AdminLocations;
